import React, { useCallback, useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import TopicKeyword from '@op/pages/Overview/KeyOpinions/OpinionsSummary/TopicKeyword';
import Abnormal from '@home/components/Abnormal';
import { JumpType, navigate } from '@home/utils/utils';
import './index.scss';

interface IProps {
  filter: any;
  filterCond: any;
  filterLoading: boolean;
  translateTopic: (text: string) => string;
  // translateTopicMap 是原始翻译 map 对象，TopicCard/KeywordCard 内部需要用 map 做 key 查找
  translateTopicMap?: Record<string, { 'en-US': string; 'zh-CN': string }>;
  gameInfo?: any;
  wholeGameInfo?: any;
  // 筛选器配置参数
  extraParams?: any;
}

const OpinionsTopicKeyword: React.FC<IProps> = ({
  filter,
  filterCond,
  filterLoading,
  translateTopicMap,
  gameInfo,
  extraParams
}) => {
  // 参照其他 studio 组件（如 OpinonsChannelLanguge、RisingTopics），
  // 清理 filter 并注入 filterCond 参数
  const cleanFilter = useMemo(() => {
    if (!filter?.date_type || !filterCond) return null;
    const { entity_image, isConfirm, ...restFilter } = filter || {};
    return {
      ...restFilter,
      social_media_by_account:
        filterCond?.social_media_by_account || undefined,
      social_media_by_keyword: undefined,
      // 和舆情保持一致，和舆情核对后，这里这两个参数需要保持一致，其他的如果没有对应筛选器，不传就行
      is_valid: [2, 1, 3],
      keyword_sample_size: "500",
      ...(extraParams || {})
    };
  }, [filter, filterCond]);

  // 跳转用的 filter 参数（日期范围）
  const filterParams = useMemo(() => {
    if (!filter) return {};
    return {
      date_type: filter?.date_type || '',
      start_time: filter?.start_time || '',
      end_time: filter?.end_time || '',
    };
  }, [filter]);

  const gameid = gameInfo?.edition_unified_id || '';

  // 跳转共用参数：日期 + is_valid，与 cleanFilter 保持一致
  const jumpParams = useMemo(() => ({
    ...filterParams,
    is_valid: [2, 1, 3],
  }), [filterParams]);

  // Topic 标题跳转：与 CommonTitle 保持一致，新窗口打开并携带 gameid
  // 额外传递 topicLevel 和 sentimentType，让落地页能正确初始化 tab 状态
  const handleTopicTitleClick = useCallback((): void => {
    const url = `/opinion/Summary/TopicTracking?gameid=${gameid}`;
    navigate(url, {
      jumpType: JumpType.Window,
      windowParams: {
        ...jumpParams,
        topicLevel: 'L2',
        sentimentType: 'total',
        chartType: 'block',
      },
    });
  }, [gameid, jumpParams]);

  // Keyword 标题跳转
  const handleKeywordTitleClick = useCallback((): void => {
    const url = `/opinion/Stats/Keyword?gameid=${gameid}`;
    navigate(url, { jumpType: JumpType.Window, windowParams: jumpParams });
  }, [gameid, jumpParams]);

  return (
    <Abnormal
      isLoading={filterLoading || !cleanFilter}
      classNames="dbh-studio-opinions-topic-keyword-abnormal"
      component={
        cleanFilter ? (
          <CardCommon className="dbh-studio-opinions-topic-keyword">
            <TopicKeyword
              filter={cleanFilter}
              translateTopic={translateTopicMap}
              curFilter="total"
              channelOption="total"
              gameInfo={gameInfo}
              handleTopicTitleClick={handleTopicTitleClick}
              handleKeywordTitleClick={handleKeywordTitleClick}
            />
          </CardCommon>
        ) : null
      }
    />
  );
};

export default OpinionsTopicKeyword;
